import { Flex, Group, Text, Title } from "@mantine/core";
import { HEADER_TEST_IDS } from "./constants";
import { ThemeToggle } from "../ThemeToggle";
import { SessionMenu } from "../SessionMenu";
import { ProgressBar } from "../ProgressBar";
import type { StaticTextTranslator } from "../../utils";
import styles from "./Header.module.css";

interface HeaderProps {
  /** Company name */
  company: string;
  /** Interview/job title */
  title: string;
  /** Session ID for session menu (optional — only shown when provided) */
  sessionId?: string;
  /** Candidate email for save progress (optional) */
  candidateEmail?: string;
  /** AS-1564: candidate-facing progress position. Hidden when undefined. */
  currentQuestionNumber?: number;
  /** AS-1564: total counted questions. Hidden when undefined. */
  totalQuestions?: number;
  /** Optional translation function passed through to SessionMenu + ProgressBar */
  translate?: StaticTextTranslator;
}

/**
 * Header component displaying company name, interview title, theme toggle,
 * and (AS-1564) the candidate-facing progress bar as a second row beneath
 * the company / title block. Matches the Mobile.html design treatment.
 */
export const Header = ({
  company,
  title,
  sessionId,
  candidateEmail,
  currentQuestionNumber,
  totalQuestions,
  translate,
}: HeaderProps) => {
  return (
    <header className={styles.headerContainer}>
      <Flex className={styles.header} direction="column" justify="flex-start">
        <Flex direction="row" justify="space-between" align="center">
          <Flex direction="column">
            <Text data-testid={HEADER_TEST_IDS.company} size="sm" c="dimmed">
              {company}
            </Text>
            <Title
              className={styles.title}
              data-testid={HEADER_TEST_IDS.title}
              order={6}
            >
              {title}
            </Title>
          </Flex>
          <Group gap="sm">
            {sessionId && (
              <SessionMenu
                sessionId={sessionId}
                candidateEmail={candidateEmail}
                translate={translate}
              />
            )}
            <ThemeToggle translate={translate} />
          </Group>
        </Flex>
        <ProgressBar
          currentQuestionNumber={currentQuestionNumber}
          totalQuestions={totalQuestions}
          translate={translate}
        />
      </Flex>
    </header>
  );
};
